import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { OrderCard, Section, BackPage, Spinner, Message, Modal } from 'components';
import { OrderItemStatusContent } from 'services/content';
import OrderItem, { orderItemStatusBadge } from './order-item';
import styles from './styles.module.css';
import {
  infoCircleTeal,
  mastercardLogo,
  visaIncLogo as visaLogo,
  americanExpressLogo,
  jcbLogo,
  discoverLogo,
  dciLogo,
  bell as bellImg,
  calendarEvent,
  chevronRightBlack,
} from 'assets';

import dateFunctions from 'helpers/dateFunction';

import { useOrderById } from 'hooks';
import { OrderPageContent } from 'services/content';

const OrderById = () => {
  const history = useHistory();
  let { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [order, getOrderById] = useOrderById();

  useEffect(() => {
    getOrderById(id);
  }, [getOrderById, id]);

  const displayCardLogo = (cardType) => {
    switch (cardType) {
      case 'MASTERCARD':
        return mastercardLogo;
      case 'VISA':
        return visaLogo;
      case 'AMERICAN EXPRESS':
        return americanExpressLogo;
      case 'JCB':
        return jcbLogo;
      case 'DISCOVER':
        return discoverLogo;
      case 'DINERS CLUB INTERNATIONAL':
        return dciLogo;
      default:
        return null;
    }
  };

  const isReminderAlreadySet = (order) => {
    return order?.reminder?.reminder_date || false;
  };

  const isOrderStatusValid = (order) => {
    const orderStatus = order?.order_statuses[0]?.code;
    return orderStatus !== 'Rejected' && orderStatus !== 'AwaitingPayment';
  };

  const orderIncludesMatching = (order) => {
    const eligibleOrders = order?.order_items.filter(
      (i) => i.match_3_status === 'Removed' || i.match_3_status === 'Added'
    );
    return eligibleOrders.length > 0;
  };

  if (!order)
    return (
      <BackPage onBackClick={() => history.goBack()} bplg="10">
        <div className={styles.loadingSpinner}>
          <Spinner />
        </div>
      </BackPage>
    );

  const orderCardData = {
    date: order.date_requested,
    id: order.order_id,
    statuses: order.order_statuses,
    trackingUrl: order.tracking_url,
  };

  return (
    <BackPage onBackClick={() => history.push(`/pharmacy/dashboard?view=order`)} bplg="10">
      {isModalOpen && (
        <Modal onClick={() => setIsModalOpen(false)}>
          <h2>{OrderItemStatusContent.header}</h2>
          <ul className={styles.statusesContainer}>
            {OrderItemStatusContent.statuses.map(({ status, description }) => {
              return (
                <>
                  {orderItemStatusBadge(status)}
                  <p className={styles.statusDescription}>{description}</p>
                </>
              );
            })}
          </ul>
          <br />
          <h2>{OrderItemStatusContent.subHeader}</h2>
          <p style={{ width: '100%', textAlign: 'left' }}>{OrderItemStatusContent.paragraphText1}</p>
          <a href={OrderItemStatusContent.cta.link} target="_blank" rel="noreferrer" className={styles.ctaLink}>
            {OrderItemStatusContent.cta.text}
          </a>
        </Modal>
      )}
      <div className={styles.grid}>
        <div>
          <h1 className={styles.header}>Your Order</h1>
          <OrderCard data={orderCardData} />
          {order.created_from === 'pharmacyUi' && (
            <Section>
              <div className={styles.infoContainer}>
                <div>
                  <p className={styles.infoContainer__paragraph}>
                    We created this order for you because we received a prescription from your GP that didn’t match an
                    order you placed through us. We’ll dispense it as written by your GP.
                  </p>
                </div>
              </div>
            </Section>
          )}
        </div>
        <div className={styles.sections}>
          {order.fulfillment.delivery_type === 'homeDelivery' &&
            !orderIncludesMatching(order) &&
            Object.keys(order).includes('total_amount') && (
              <Section
                data-testid="total-amount-container"
                title={
                  order.card_detail
                    ? orderCardData.statuses[0].code === 'OutForDelivery'
                      ? 'Total'
                      : 'Estimate Total'
                    : 'Total'
                }>
                <div className={`${styles.spacingTop} ${styles.sizing}`}>
                  {order.total_amount === 0 ? 'Free' : `£${order.total_amount.toFixed(2)}`}
                </div>
                {!order?.exemption?.name && order?.total_amount !== 0 && (
                  <Message type="order" icon={infoCircleTeal} text={OrderPageContent.authorisationText} />
                )}
              </Section>
            )}
          {order.order_items.length > 0 && (
            <Section title="Your medication request">
              <ul className={styles.medications}>
                {order.order_items.map((item) => {
                  return <OrderItem data={item} key={item.course_id} />;
                })}
                {orderIncludesMatching(order) && (
                  <p
                    data-testid="order-status-modal-button"
                    className={styles.orderItemStatusModalButton}
                    onClick={() => setIsModalOpen(true)}>
                    Further details about your item status
                  </p>
                )}
              </ul>
              {isReminderAlreadySet(order) ? (
                <div className={styles.reminderContainer}>
                  <img src={calendarEvent} alt="Calendar" />
                  <p>{`Reminder set for ${dateFunctions.formatDateWithNthNumber(
                    new Date(order.reminder.reminder_date)
                  )} `}</p>
                </div>
              ) : isOrderStatusValid(order) ? (
                <button
                  className={styles.reminderButton}
                  onClick={() => history.push(`/pharmacy/dashboard/order-reminder/${order.order_id}`)}>
                  <img src={bellImg} alt="Reminder bell" />
                  <p>Create a reminder for this order</p>
                  <img src={chevronRightBlack} alt="Chevron" />
                </button>
              ) : null}
            </Section>
          )}
          <Section
            title={
              order.fulfillment.delivery_type === 'homeDelivery' ||
              order.fulfillment.delivery_type === 'pharmacyDelivery'
                ? 'Delivery address'
                : 'Collection address'
            }>
            <address className={styles.address}>
              {order.fulfillment.delivery_type !== 'pharmacyDelivery' &&
              order.fulfillment.delivery_type !== 'homeDelivery'
                ? <span className={styles.addressName}>{order.fulfillment.pharmacy_name}</span> || null
                : null}
              {order.fulfillment.delivery_type !== 'pharmacyDelivery' &&
              order.fulfillment.delivery_type !== 'homeDelivery'
                ? order.fulfillment.pharmacy_name && <br />
                : null}
              {order.fulfillment.address.address_line1 || null}
              {order.fulfillment.address.address_line1 && <br />}
              {order.fulfillment.address.address_line2 || null}
              {order.fulfillment.address.address_line2 && <br />}
              {order.fulfillment.address.address_line3 || null}
              {order.fulfillment.address.address_line3 && <br />}
              {order.fulfillment.address.postcode || null}
            </address>
          </Section>
          {!orderIncludesMatching(order) && order.card_detail && (
            <Section title="Payment">
              <div className={styles.payment}>
                <img
                  src={displayCardLogo(order.card_detail.type)}
                  alt="payment icon"
                  className={styles.paymentLogoImg}
                />
                <div>••••{order.card_detail.number}</div>
                <div className={styles.paymentExpiry}>
                  {order.card_detail.expiry.slice(0, 2)}/{order.card_detail.expiry.slice(2, 4)}
                </div>
              </div>
            </Section>
          )}
          {order.exemption?.name && (
            <Section title="Exemption reason">
              <div className={`${styles.spacingTop} ${styles.sizing}`}>{order.exemption.name}</div>
            </Section>
          )}
          {order.gp_notes && (
            <Section title="Notes to your GP">
              <div className={`${styles.spacingTop} ${styles.sizing}`}>{order.gp_notes}</div>
            </Section>
          )}
        </div>
      </div>
    </BackPage>
  );
};

export default OrderById;
